import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getFirestore, collection, query, where, onSnapshot } from 'firebase/firestore';
import { getDataCompletenessAnalysis, uploadCCBProfileImageV2 } from '../firebase/functionsService';
import { fetchVolunteerCheckInHistory, fetchVolunteerNotes, updateParticipant, subscribeToUnreadVolunteerNotes, markVolunteerNotesAsRead, volunteerNoteKey } from '../firebase/firestoreService';
import { getCurrentUser, getUserRole } from '../firebase/authService';
import { getServiceType, VOLUNTEER_GROUP_ID } from '../utils/roles';
import AddNoteModal from '../components/AddNoteModal';
import EditGuestModal from '../components/EditGuestModal';
import { useAppBusy } from '../utils/appUpdate';
import placeholderAvatar from '../assets/placeholder-avatar.svg';
import '../styles/GuestProfiles.css';
import '../styles/VolunteerProfiles.css';
import { readOnlyProps } from '../utils/viewAs';

function VolunteerProfiles() {
  const ro = readOnlyProps(); // disable write controls while viewing as someone
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [volunteers, setVolunteers] = useState([]);
  const [filteredVolunteers, setFilteredVolunteers] = useState([]);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage] = useState(10);
  const [volunteerHistory, setVolunteerHistory] = useState({});
  const [volunteerProfileNotes, setVolunteerProfileNotes] = useState({}); // volunteerId -> profile notes
  const [userRole, setUserRole] = useState(null);
  const [selectedForEdit, setSelectedForEdit] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedForNote, setSelectedForNote] = useState(null);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [uploadingPhotoFor, setUploadingPhotoFor] = useState(null);
  useAppBusy('volunteer-profiles-photo', uploadingPhotoFor !== null);
  const [photoUploadMessage, setPhotoUploadMessage] = useState(null);
  const [placeholderImages, setPlaceholderImages] = useState(new Set());
  const [failedImages, setFailedImages] = useState(new Set());
  const [showNotesOnly, setShowNotesOnly] = useState(false); // unread-notes-only view
  const [unreadNoteCounts, setUnreadNoteCounts] = useState({}); // volunteerId -> unread count for this user
  const shouldResetPage = useRef(false);
  const currentUser = getCurrentUser();

  const fieldsToCheck = [
    'firstName', 'lastName', 'gender', 'birthday',
    'phone', 'mobilePhone', 'homePhone', 'email',
    'street', 'city', 'state', 'zip',
    'emergencyContactName', 'emergencyContactPhone',
    'image'
  ];

  const hasRealImage = (v) => v.image && !placeholderImages.has(v.id) && !failedImages.has(v.id);

  const handleAvatarLoad = (id, event) => {
    const img = event.target;
    if (img.naturalWidth === 600 && img.naturalHeight === 400) {
      setPlaceholderImages(prev => new Set(prev).add(id));
    }
  };

  const handleAvatarError = (id) => {
    setFailedImages(prev => new Set(prev).add(id));
  };

  useEffect(() => {
    const loadUserRole = async () => {
      if (currentUser) {
        const role = await getUserRole(currentUser.uid);
        setUserRole(role);
      }
    };
    loadUserRole();
  }, [currentUser]);

  // Real-time listener for participants with completeness
  useEffect(() => {
    const db = getFirestore();
    const participantsRef = collection(db, 'participants');
    // Use array-contains so cross-group members (e.g. someone in both 1269
     // and 715) show up in the volunteer roster even if their legacy
     // `groupId` field anchors them to the guest group.
    const q = query(participantsRef, where('groupIds', 'array-contains', VOLUNTEER_GROUP_ID));

    setLoading(true);
    setError(null);

    const unsubscribe = onSnapshot(
      q,
      async (snapshot) => {
        try {
          // Inactive guests (CCB-deactivated) are hidden — CCB is the
          // source of truth for status.
          const allParticipants = snapshot.docs
            .map(doc => ({
              id: doc.id,
              ...doc.data()
            }))
            .filter(p => p.inactive !== true);

          // Get completeness analysis
          const analysisResult = await getDataCompletenessAnalysis(VOLUNTEER_GROUP_ID);
          const incompleteMap = new Map();
          if (analysisResult.incompleteProfiles) {
            analysisResult.incompleteProfiles.forEach(profile => {
              incompleteMap.set(profile.id, profile);
            });
          }

          const processed = allParticipants.map(participant => {
            const incompleteData = incompleteMap.get(participant.id);
            if (incompleteData) {
              return { ...participant, ...incompleteData, isComplete: false };
            }
            let filledFields = 0;
            fieldsToCheck.forEach(field => {
              if (participant[field] && participant[field].toString().trim() !== '') {
                filledFields++;
              }
            });
            const completeness = Math.round((filledFields / fieldsToCheck.length) * 100);
            return {
              ...participant,
              completeness,
              missingFields: [],
              criticalMissing: false,
              emergencyContactMissing: !participant.emergencyContactName || !participant.emergencyContactPhone,
              isComplete: completeness === 100
            };
          });

          processed.sort((a, b) =>
            `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`)
          );

          setVolunteers(processed);
          setLoading(false);
        } catch (err) {
          console.error('Error processing volunteers:', err);
          setError(err.message || 'Failed to process volunteer profiles');
          setLoading(false);
        }
      },
      (err) => {
        console.error('Error listening to participants:', err);
        setError(err.message || 'Failed to load volunteer profiles');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  // Recalculate completeness when placeholder images detected
  useEffect(() => {
    if (placeholderImages.size === 0) return;
    setVolunteers(prev => prev.map(v => {
      if (!placeholderImages.has(v.id)) return v;
      let filledFields = 0;
      fieldsToCheck.forEach(field => {
        if (field === 'image') return;
        if (v[field] && v[field].toString().trim() !== '') filledFields++;
      });
      const completeness = Math.round((filledFields / fieldsToCheck.length) * 100);
      return { ...v, completeness, isComplete: completeness === 100 };
    }));
  }, [placeholderImages]);

  // Real-time unread volunteer notes for this user
  useEffect(() => {
    if (!currentUser) return;
    const unsubscribe = subscribeToUnreadVolunteerNotes(currentUser.uid, setUnreadNoteCounts);
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid]);

  const unreadVolunteerCount = Object.values(unreadNoteCounts).filter(n => n > 0).length;

  // The header bells link here with ?unread=true
  useEffect(() => {
    if (searchParams.get('unread') === 'true') {
      shouldResetPage.current = true;
      setShowNotesOnly(true);
    }
  }, [searchParams]);

  // Filter
  useEffect(() => {
    let filtered = [...volunteers];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(v =>
        `${v.firstName} ${v.lastName}`.toLowerCase().includes(term) ||
        v.email?.toLowerCase().includes(term) ||
        v.phone?.includes(term)
      );
    }

    if (filterStatus === 'complete') {
      filtered = filtered.filter(v => v.completeness === 100);
    } else if (filterStatus === 'incomplete') {
      filtered = filtered.filter(v => v.completeness < 100);
    }

    // Unread-only view. An open row stays visible after its notes are marked
    // read, so it doesn't vanish while being looked at; it drops out on collapse.
    if (showNotesOnly) {
      filtered = filtered.filter(v => unreadNoteCounts[v.id] > 0 || expandedRows.has(v.id));
    }

    setFilteredVolunteers(filtered);
    if (shouldResetPage.current) {
      setCurrentPage(1);
      shouldResetPage.current = false;
    }
  }, [searchTerm, filterStatus, showNotesOnly, volunteers, unreadNoteCounts, expandedRows]);

  const toggleRow = async (volunteerId) => {
    const newExpanded = new Set(expandedRows);
    if (!newExpanded.has(volunteerId)) {
      newExpanded.clear();
      newExpanded.add(volunteerId);
      try {
        const [history, profileNotes] = await Promise.all([
          fetchVolunteerCheckInHistory(volunteerId),
          fetchVolunteerNotes(volunteerId)
        ]);
        setVolunteerHistory(prev => ({ ...prev, [volunteerId]: history }));
        setVolunteerProfileNotes(prev => ({ ...prev, [volunteerId]: profileNotes }));

        // Opening the row shows its notes, so mark them read for this user
        const noteKeys = [
          ...history.filter(r => r.notes && r.notes.trim()).map(r => volunteerNoteKey('checkin', r.id)),
          ...profileNotes.map(n => volunteerNoteKey('profile', n.id))
        ];
        if (currentUser && noteKeys.length > 0) {
          markVolunteerNotesAsRead(
            currentUser.uid,
            volunteerId,
            noteKeys,
            currentUser.displayName || currentUser.email
          ).catch(err => console.error('Error marking volunteer notes as read:', err));
        }
      } catch (err) {
        console.error('Error loading volunteer history:', err);
      }
    } else {
      newExpanded.delete(volunteerId);
    }
    setExpandedRows(newExpanded);
  };

  const getServiceLabel = (serviceId) => {
    const info = getServiceType(serviceId);
    if (info) return info.name;
    if (serviceId === 'unscheduled') return 'Off-schedule';
    return serviceId || '';
  };

  const formatDateTimeParts = (timestamp) => {
    if (!timestamp) return { day: '', date: '', time: '' };
    const d = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return {
      day: d.toLocaleDateString('en-US', { weekday: 'short' }),
      date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      time: d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    };
  };

  const getCompletenessColor = (pct) => {
    if (pct >= 90) return '#10b981';
    if (pct >= 70) return '#f59e0b';
    return '#ef4444';
  };

  const renderFieldValue = (value, fieldName) => {
    if (value && value.toString().trim() !== '') {
      return (
        <div className="field-present">
          <span className="field-icon">&#10003;</span>
          <span className="field-label-prefix">{fieldName}:</span>
          <span className="field-value">{value}</span>
        </div>
      );
    }
    return (
      <div className="field-missing">
        <span className="field-icon">&#10007;</span>
        <span className="field-label">{fieldName}:</span>
        <span className="field-missing-text">(missing)</span>
      </div>
    );
  };

  // Photo upload
  const handleUploadPhotoToCCB = async (volunteer) => {
    if (!volunteer.image || !volunteer.id.startsWith('ccb_')) return;
    if (!expandedRows.has(volunteer.id)) toggleRow(volunteer.id);
    setUploadingPhotoFor(volunteer.id);
    setPhotoUploadMessage(null);
    try {
      const numericId = volunteer.id.replace('ccb_', '');
      await uploadCCBProfileImageV2({ individualId: numericId, imageUrl: volunteer.image });
      await updateParticipant(volunteer.id, { ccbImageUploaded: true });
      setPhotoUploadMessage({ id: volunteer.id, type: 'success', text: 'Photo uploaded to CCB!' });
      setTimeout(() => setPhotoUploadMessage(null), 3000);
    } catch (error) {
      console.error('Error uploading photo to CCB:', error);
      setPhotoUploadMessage({ id: volunteer.id, type: 'error', text: `Upload failed: ${error.message}` });
    } finally {
      setUploadingPhotoFor(null);
    }
  };

  // CSV export
  const exportToCSV = () => {
    if (filteredVolunteers.length === 0) return;
    const headers = ['First Name', 'Last Name', 'Gender', 'Birthday', 'Email', 'Phone', 'Mobile Phone', 'Home Phone', 'Street', 'City', 'State', 'Zip', 'Emergency Contact', 'Emergency Phone', 'Completeness %'];
    const rows = filteredVolunteers.map(v => [
      v.firstName || '', v.lastName || '', v.gender || '', v.birthday || '',
      v.email || '', v.phone || '', v.mobilePhone || '', v.homePhone || '',
      v.street || '', v.city || '', v.state || '', v.zip || '',
      v.emergencyContactName || '', v.emergencyContactPhone || '',
      v.completeness || 0
    ]);
    const csvContent = [headers.join(','), ...rows.map(row => row.map(cell => `"${cell}"`).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `volunteer-profiles-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const isSysAdmin = userRole === 'sys_admin';
  const canEdit = userRole === 'sys_admin' || userRole === 'aar_leader' || userRole === 'bt_admin';

  // Pagination
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredVolunteers.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(filteredVolunteers.length / recordsPerPage);

  const scrollToTable = () => {
    const el = document.querySelector('.profiles-table-section');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const paginate = (n) => { setCurrentPage(n); setExpandedRows(new Set()); scrollToTable(); };
  const goToNextPage = () => { if (currentPage < totalPages) { setCurrentPage(currentPage + 1); setExpandedRows(new Set()); scrollToTable(); } };
  const goToPrevPage = () => { if (currentPage > 1) { setCurrentPage(currentPage - 1); setExpandedRows(new Set()); scrollToTable(); } };

  const renderPagination = () => (
    <div className="pagination-controls">
      <button className="pagination-btn" onClick={goToPrevPage} disabled={currentPage === 1}>&laquo; Previous</button>
      {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => {
        if (p === 1 || p === totalPages || (p >= currentPage - 1 && p <= currentPage + 1)) {
          return <button key={p} className={`pagination-btn ${currentPage === p ? 'active' : ''}`} onClick={() => paginate(p)}>{p}</button>;
        } else if (p === currentPage - 2 || p === currentPage + 2) {
          return <span key={p} className="pagination-ellipsis">...</span>;
        }
        return null;
      })}
      <button className="pagination-btn" onClick={goToNextPage} disabled={currentPage === totalPages}>Next &raquo;</button>
    </div>
  );

  if (loading) return <div className="loading"><div className="spinner"></div><p>Loading volunteer profiles...</p></div>;
  if (error) return <div className="guest-profiles-container volunteer-profiles-container"><div className="error-message"><h3>Error Loading Profiles</h3><p>{error}</p><button className="btn btn-primary" onClick={() => window.location.reload()}>Retry</button></div></div>;

  return (
    <div className="guest-profiles-container volunteer-profiles-container">
      <div className="guest-profiles-header">
        <button className="btn-back" onClick={() => navigate('/volunteer-check-in')}>&larr; Volunteer Check-In</button>
        <h2>Volunteer Profiles</h2>
      </div>

      {/* Controls Bar */}
      <div className="controls-bar">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by name, email, or phone..."
            value={searchTerm}
            onChange={(e) => { shouldResetPage.current = true; setSearchTerm(e.target.value); }}
            className="search-input"
          />
        </div>

        <select
          value={filterStatus}
          onChange={(e) => { shouldResetPage.current = true; setSearchTerm(''); setFilterStatus(e.target.value); }}
          className="filter-select"
        >
          <option value="all">All Volunteers ({volunteers.length})</option>
          <option value="complete">Complete Profiles ({volunteers.filter(v => v.completeness === 100).length})</option>
          <option value="incomplete">Incomplete Profiles ({volunteers.filter(v => v.completeness < 100).length})</option>
        </select>

        <button
          className="btn-bell"
          onClick={() => {
            if (!showNotesOnly) {
              shouldResetPage.current = true;
              setShowNotesOnly(true);
            }
          }}
          title={unreadVolunteerCount > 0 ? `${unreadVolunteerCount} volunteer${unreadVolunteerCount > 1 ? 's' : ''} with unread notes` : 'No unread notes'}
        >
          🔔
          {unreadVolunteerCount > 0 && (
            <span className="bell-count">{unreadVolunteerCount}</span>
          )}
        </button>

        {showNotesOnly && (
          <button
            className="btn btn-secondary"
            onClick={() => {
              shouldResetPage.current = true;
              setShowNotesOnly(false);
              if (searchParams.get('unread')) navigate('/volunteer-profiles');
            }}
          >
            Show All
          </button>
        )}

        <button className="btn btn-primary" onClick={exportToCSV}>Export to CSV</button>
      </div>

      {/* Table */}
      <div className="profiles-table-section">
        {filteredVolunteers.length > recordsPerPage && (
          <div className="pagination-container top">
            <div className="pagination-info">
              Showing {indexOfFirstRecord + 1} to {Math.min(indexOfLastRecord, filteredVolunteers.length)} of {filteredVolunteers.length} volunteers
            </div>
            {renderPagination()}
          </div>
        )}

        {filteredVolunteers.length === 0 ? (
          <div className="empty-state"><p>No volunteers found matching your criteria.</p></div>
        ) : (
          <table className="profiles-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Contact</th>
                <th>Completeness</th>
                <th>Status</th>
                <th className="actions-column">Actions</th>
                <th className="expand-column"></th>
              </tr>
            </thead>
            <tbody>
              {currentRecords.map((volunteer) => {
                const isExpanded = expandedRows.has(volunteer.id);
                const history = volunteerHistory[volunteer.id] || [];
                const profileNotes = volunteerProfileNotes[volunteer.id] || [];
                return (
                  <React.Fragment key={volunteer.id}>
                    <tr
                      id={`volunteer-row-${volunteer.id}`}
                      className={`guest-row ${isExpanded ? 'expanded' : ''}`}
                      onClick={() => toggleRow(volunteer.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td className="guest-name">
                        <div className="name-cell">
                          <div className="guest-avatar">
                            {hasRealImage(volunteer) ? (
                              <img src={volunteer.image} alt={`${volunteer.firstName} ${volunteer.lastName}`} onLoad={(e) => handleAvatarLoad(volunteer.id, e)} onError={() => handleAvatarError(volunteer.id)} />
                            ) : (
                              <>
                                {volunteer.image && !failedImages.has(volunteer.id) && (
                                  <img src={volunteer.image} alt="" onLoad={(e) => handleAvatarLoad(volunteer.id, e)} onError={() => handleAvatarError(volunteer.id)} style={{ display: 'none' }} />
                                )}
                                <img src={placeholderAvatar} alt={`${volunteer.firstName} ${volunteer.lastName}`} className="avatar-placeholder-img" />
                              </>
                            )}
                          </div>
                          <div className="name-text">
                            {volunteer.firstName} {volunteer.lastName}
                            {volunteer.id?.startsWith('ccb_') && (
                              <a
                                className="ccb-id-link"
                                href={`https://mybt.ccbchurch.com/goto/individuals/${volunteer.id.replace('ccb_', '')}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                title="Open profile in CCB"
                              >
                                CCB ID: {volunteer.id.replace('ccb_', '')}
                              </a>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="contact-info">
                          <div>{volunteer.email || 'No email'}</div>
                          <div>{volunteer.phone || 'No phone'}</div>
                        </div>
                      </td>
                      <td>
                        <div className="completeness-cell">
                          <span className="completeness-percentage" style={{ color: getCompletenessColor(volunteer.completeness) }}>
                            {volunteer.completeness}%
                          </span>
                          <div className="progress-bar-container">
                            <div className="progress-bar-fill" style={{ width: `${volunteer.completeness}%`, backgroundColor: getCompletenessColor(volunteer.completeness) }} />
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="status-cell">
                          {volunteer.criticalMissing && <span className="status-badge critical">Critical Missing</span>}
                          {volunteer.emergencyContactMissing && <span className="status-badge emergency">No Emergency Contact</span>}
                          {!volunteer.criticalMissing && !volunteer.emergencyContactMissing && volunteer.completeness === 100 && <span className="status-badge complete">Complete</span>}
                        </div>
                      </td>
                      <td className="actions-column">
                        <div className="action-buttons">
                          <button
                            className="btn-add-note"
                            onClick={(e) => { e.stopPropagation(); setSelectedForNote(volunteer); setShowNoteModal(true); }}
                            disabled={ro.disabled}
                            aria-label="Add note"
                            title={ro.title || 'Add note to volunteer profile'}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/></svg>
                          </button>
                          <button
                            className="btn-upload-ccb"
                            onClick={(e) => { e.stopPropagation(); handleUploadPhotoToCCB(volunteer); }}
                            disabled={uploadingPhotoFor === volunteer.id || ro.disabled}
                            aria-label="Upload photo to CCB"
                            title={ro.title || 'Upload photo to CCB'}
                          >
                            {uploadingPhotoFor === volunteer.id ? '...' : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>}
                          </button>
                          <button
                            className="btn-edit"
                            onClick={(e) => { e.stopPropagation(); setSelectedForEdit(volunteer); setShowEditModal(true); }}
                            disabled={ro.disabled}
                            aria-label="Edit profile"
                            title={ro.title || 'Edit volunteer profile'}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                          </button>
                        </div>
                      </td>
                      <td className="expand-column">
                        <button className={`expand-btn ${isExpanded ? 'expanded' : ''}`} onClick={(e) => { e.stopPropagation(); toggleRow(volunteer.id); }} aria-label={isExpanded ? 'Collapse' : 'Expand'}>&#9660;</button>
                      </td>
                    </tr>

                    {/* Expanded Details */}
                    {isExpanded && (
                      <tr className="details-row">
                        <td colSpan="6">
                          <div className="details-content">
                            <div className="details-grid">
                              <div className="detail-section">
                                <h4>Personal Information</h4>
                                <div className="detail-fields">
                                  {renderFieldValue(volunteer.firstName, 'First')}
                                  {renderFieldValue(volunteer.lastName, 'Last')}
                                  {renderFieldValue(volunteer.gender === 'm' ? 'Male' : volunteer.gender === 'f' ? 'Female' : volunteer.gender, 'Gender')}
                                  {renderFieldValue(volunteer.birthday, 'DOB')}
                                  {renderFieldValue(hasRealImage(volunteer) ? 'Yes' : '', 'Photo')}
                                </div>
                              </div>
                              <div className="detail-section">
                                <h4>Contact Information</h4>
                                <div className="detail-fields">
                                  {renderFieldValue(volunteer.email, 'Email')}
                                  {renderFieldValue(volunteer.phone, 'Phone')}
                                  {renderFieldValue(volunteer.mobilePhone, 'Mobile')}
                                  {renderFieldValue(volunteer.homePhone, 'Home')}
                                </div>
                              </div>
                              <div className="detail-section">
                                <h4>Address Information</h4>
                                <div className="detail-fields">
                                  {renderFieldValue(volunteer.street, 'Street')}
                                  {renderFieldValue(volunteer.city, 'City')}
                                  {renderFieldValue(volunteer.state, 'State')}
                                  {renderFieldValue(volunteer.zip, 'Zip')}
                                </div>
                              </div>
                              <div className="detail-section">
                                <h4>Emergency Contact</h4>
                                <div className="detail-fields">
                                  {renderFieldValue(volunteer.emergencyContactName, 'Name')}
                                  {renderFieldValue(volunteer.emergencyContactPhone, 'Phone')}
                                </div>
                              </div>
                            </div>

                            {/* Profile Notes */}
                            {profileNotes.length > 0 && (
                              <div className="notes-history-section">
                                <h4>Notes</h4>
                                <div className="notes-timeline">
                                  {profileNotes.map((note) => {
                                    const authorName = note.createdByName || 'Unknown';
                                    return (
                                      <div key={note.id} className="note-entry-compact">
                                        <span className="note-author-avatar-compact">{authorName.charAt(0) || '?'}</span>
                                        <span className="note-author-name-compact">{authorName}</span>
                                        <span className="note-source-badge note-source-profile">Profile Note</span>
                                        <span className="note-content-compact">{note.note}</span>
                                        <span className="note-separator">•</span>
                                        <span className="note-timestamp-compact">
                                          {note.createdAt?.toDate ? note.createdAt.toDate().toLocaleString('en-US', {
                                            month: 'short',
                                            day: 'numeric',
                                            year: 'numeric',
                                            hour: 'numeric',
                                            minute: '2-digit',
                                            hour12: true
                                          }) : 'Just now'}
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}

                            {/* Check-In History */}
                            {history.length > 0 ? (
                              <div className="vol-history-section">
                                <h4>Check-In History</h4>
                                <div className="vol-history-header">
                                  <span className="vol-col-who">Checked In By</span>
                                  <span className="vol-col-notes">Notes</span>
                                  <span className="vol-col-time">Date / Time</span>
                                </div>
                                <div className="vol-history-list">
                                  {history.map((record) => (
                                    <div key={record.id} className="vol-history-row">
                                      <div className="vol-col-who">
                                        <span className="vol-author-avatar">{(record.checkInByName || '?').charAt(0)}</span>
                                        <span className="vol-author-name">{record.checkInByName || 'Unknown'}</span>
                                        {record.serviceType && record.serviceType !== 'unscheduled' && (
                                          <span className="vol-service-tag">{getServiceLabel(record.serviceType)}</span>
                                        )}
                                      </div>
                                      <div className="vol-col-notes">
                                        {record.notes ? <span className="vol-note-text">{record.notes}</span> : <span className="vol-note-empty">--</span>}
                                      </div>
                                      <div className="vol-col-time">
                                        {(() => {
                                          const t = formatDateTimeParts(record.checkInTime);
                                          return (<><span className="vol-time-day">{t.day}</span><span className="vol-time-date">{t.date}</span><span className="vol-time-clock">{t.time}</span></>);
                                        })()}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              <div style={{ padding: '20px', textAlign: 'center', color: '#9ca3af', fontSize: '14px' }}>
                                No check-in history found for this volunteer.
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}

        {filteredVolunteers.length > recordsPerPage && (
          <div className="pagination-container">
            <div className="pagination-info">
              Showing {indexOfFirstRecord + 1} to {Math.min(indexOfLastRecord, filteredVolunteers.length)} of {filteredVolunteers.length} volunteers
            </div>
            {renderPagination()}
          </div>
        )}
      </div>

      {/* Add Note Modal */}
      {showNoteModal && selectedForNote && (
        <AddNoteModal
          guest={selectedForNote}
          kind="volunteer"
          onClose={() => { setShowNoteModal(false); setSelectedForNote(null); }}
          onSave={() => {
            // Refresh the open row so the new note appears without a re-expand
            const id = selectedForNote.id;
            fetchVolunteerNotes(id).then(notes =>
              setVolunteerProfileNotes(prev => ({ ...prev, [id]: notes }))
            );
            setShowNoteModal(false);
            setSelectedForNote(null);
          }}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && selectedForEdit && (
        <EditGuestModal
          guest={selectedForEdit}
          onClose={() => { setShowEditModal(false); setSelectedForEdit(null); }}
          onSave={() => { setShowEditModal(false); setSelectedForEdit(null); }}
        />
      )}

    </div>
  );
}

export default VolunteerProfiles;
